import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ForgotPasswordFormSchema, forgotPasswordSchema } from './formSchema';
import { AuthenticationBaseLayout } from '../../components/ui/authentication/authenticationBaseLayout';
import { useForgotPassword } from '@/services/api/users/mutations';
import { useToast } from '@/hooks/useToast';

export const ForgotPassword = () => {
  const form = useForm<ForgotPasswordFormSchema>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutate: forgotPassword, isPending } = useForgotPassword(
    () => {
      toast({
        title: 'Email Sent',
      });
      navigate('/check-email');
    },
    (error) => {
      toast({
        title: 'Error',
        description: 'Please check your email and try again.',
      });
      console.error(error);
    }
  );

  const onSubmit = (data: { email: string }) => {
    forgotPassword(data.email);
    form.reset();
  };

  return (
    <AuthenticationBaseLayout>
      <div className="flex flex-col items-start w-full max-w-sm p-6 lg:w-auto lg:max-w-none">
        <div className="flex flex-col justify-start items-start mb-4 text-left">
          <h1 className="text-base font-bold text-card-foreground">
            Forgot your Password?
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Enter your email and we’ll send you instructions to <br />
            reset your password.
          </p>
        </div>

        <Form {...form}>
          <form
            className="w-full lg:w-auto"
            onSubmit={form.handleSubmit(onSubmit as SubmitHandler<FieldValues>)}
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="text-left">
                  <FormLabel className="text-card-foreground text-sm">
                    Email *
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="w-full lg:w-[352px]"
                      placeholder="example@email.com"
                      type="email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={
                  isPending ||
                  !form.formState.isValid ||
                  form.formState.isSubmitting
                }
              >
                {isPending ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </AuthenticationBaseLayout>
  );
};
